import React, {useEffect} from "react"
import {useLocation} from "react-router-dom"

import {useKeycloak} from "@react-keycloak/web"

import {ensureFreshToken} from "@auth/utils/token"

import Loader from "src/utils/Loader"
import Routing from "src/utils/Routing"

/**
 * How often the session is kept alive, in ms.
 *
 * `ensureFreshToken` is a no-op while the token still has comfortable validity,
 * so this costs a network call only near expiry. Its real job is to make sure
 * *something* keeps calling Keycloak's token endpoint even when the user neither
 * navigates nor triggers a request — that endpoint is what re-arms the realm's
 * SSO idle timer. Without it, an idle-but-open tab lost its session and came
 * back to a full login screen (with an MFA challenge, once MFA is on).
 */
const SESSION_KEEP_ALIVE_MS = 60_000

/**
 * Gate that guarantees an authenticated session before rendering the app, and
 * keeps that session alive for as long as the app is mounted.
 *
 * Offline routes render straight through — they are the pages that must work
 * without a session at all.
 */
function AuthKeycloak({children}: any) {
    const {keycloak, initialized} = useKeycloak()
    const location = useLocation()

    const isOffline = Routing.IsOffline(location.pathname)
    const isAuthenticated = Boolean(keycloak.authenticated)

    // Send the user to Keycloak when there is no session to work with.
    useEffect(() => {
        if (!initialized || isOffline || isAuthenticated) {
            return
        }

        // `login` resolves only by navigating away, so nothing follows it here.
        keycloak.login({redirectUri: window.location.href}).catch((error) => {
            console.error("Erreur de connexion à Keycloak:", error)
        })
    }, [initialized, isOffline, isAuthenticated, keycloak])

    // Keep the session alive while the app is open.
    useEffect(() => {
        if (!initialized || isOffline || !isAuthenticated) {
            return
        }

        let cancelled = false

        const keepAlive = () => {
            if (!cancelled) {
                void ensureFreshToken()
            }
        }

        keepAlive()
        const timer = setInterval(keepAlive, SESSION_KEEP_ALIVE_MS)

        return () => {
            cancelled = true
            clearInterval(timer)
        }
    }, [initialized, isOffline, isAuthenticated])

    if (isOffline || isAuthenticated) {
        return children
    }

    return <Loader isLoading={true} style={{zIndex: 999999}} />
}

export default AuthKeycloak
